import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

/**
 * * Componente padrão para o player de animação do Apolo.
 */
@Component({
  selector: 'ui-default-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './default-player.component.html',
  styleUrls: ['./default-player.component.scss']
})
export class DefaultPlayerComponent implements OnInit, OnDestroy {

  /**
   * * Número que indica o passo atual do Player.
   */
   progress: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  /**
   * * Flag que indica se a animação está ativa.
   */
  @Input() isAnimating = false;

  /**
   * * Valor do último passo.
   */
  @Input() maxStep = 1;

  /**
   * * Valor do primeiro passo.
   */
  @Input() minStep = 0;

  /**
   * * Texto do player.
   */
  @Input() labelInfo: BehaviorSubject<string> = new BehaviorSubject('Animação');

  /**
   * * Fator de multiplicação para a animação (milisegundos).
   */
  @Input() delayFactor: number = 1000;

  /**
   * * Velocidade do player.
   */
  velocity = 1;

  private eventsSubscription: Subscription | undefined;

  /**
   * * Evento a ser ativado ao trocar de passo.
   */
  @Output() onchangestep: EventEmitter<number> = new EventEmitter();
  /**
   * * Evento a ser ativado ao iniciar a animação.
   */
  @Output() ontoggleanimation: EventEmitter<boolean> = new EventEmitter();
  /**
   * * Evento a ser ativado ao mudar a velocidade.
   */
  @Output() onchangevelocity: EventEmitter<number> = new EventEmitter();

  private animationInterval: any = null;

  constructor() { }

  ngOnInit(): void {
    this.eventsSubscription = this.progress.
      subscribe(progress => {
        this.onchangestep.next(progress);
      });
  }

  ngOnDestroy(): void {
    if (this.eventsSubscription) {
      this.eventsSubscription.unsubscribe();
    }
  }

  /**
   * * Parar animação.
   */
  stop(): void {
    this.isAnimating = false;
    this.clearAnimation();
    this.velocity = 1;
    this.toFirst();
  }

  /**
   * * Ir para o primeiro passo.
   */
  toFirst(): void {
    this.progress.next(this.minStep);
  }

  /**
   * * Retornar para o passo anterior.
   */
  toPrevious(): void {
    const currentStep = this.progress.value;
    const nextStep = this.minStep < currentStep ? currentStep - 1 : this.maxStep;
    this.progress.next(nextStep);
  }

  /**
   * * Ligar/Desligar a animação.
   */
  toggleAnimation(): void {
    this.isAnimating = !this.isAnimating;
    if (this.isAnimating) {
      this.doAnimate();
    } else {
      this.clearAnimation();
    }
    this.ontoggleanimation.emit(this.isAnimating);
  }

  /**
   * * Avançar para o próximo passo.
   */
  toNext(): void {
    const currentStep = this.progress.value;
    const nextStep = this.maxStep > currentStep ? currentStep + 1 : this.minStep;
    this.progress.next(nextStep);
  }

  /**
   * * Avançar para o último passo.
   */
  toLast(): void {
    this.progress.next(this.maxStep);
  }

  /**
   * * Alterar velocidade do player.
   */
  onChangeVelocity(): void {
    this.velocity = this.velocity < 3 ? this.velocity + 1 : 1;
    if (this.isAnimating) {
      this.doAnimate();
      this.onchangevelocity.emit(this.timerDelay);
    }
  }

  private clearAnimation(): void {
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
      this.animationInterval = null;
    }
  }

  private doAnimate() {
    this.clearAnimation();
    this.animationInterval = setInterval(() => this.toNext(), this.timerDelay);
  }

  private get timerDelay(): number {
    let timerDelay = 0;
    switch (this.velocity) {
      case 2:
        timerDelay = 0.5;
        break;
      case 3:
        timerDelay = 0.25;
        break;
      default:
        timerDelay = 1;
    }
    return timerDelay * this.delayFactor;
  }

}
